<template>
  <Select :model-value="selectedProgramIdRef" @update:model-value="handleSwitchProgram">
    <SelectTrigger data-test-id="menu-program-mentorat" class="w-full" :aria-label="t('common.program.select')">
      <FlagIcon class="size-4 shrink-0" />
      <SelectValue :placeholder="t('common.program.select')" />
    </SelectTrigger>
    <SelectContent class="z-[1000]">
      <SelectItem
        v-for="program in programsRef"
        :key="program.id"
        :value="program.id"
        data-test-id="popover-row"
      >
        {{ program.name }}
      </SelectItem>
    </SelectContent>
  </Select>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useQuery } from '@tanstack/vue-query'

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { FlagIcon } from 'lucide-vue-next'
import useAccount from '@/features/auth/hooks/use-account.hook'
import authStore from '@/features/auth/stores/auth/auth.store'
import usePrograms from '@/features/programs/hooks/use-programs.hook'
import {
  selectGetSelectedProgramId,
} from '@/features/programs/stores/programs/programs.selectors'
import programsStore from '@/features/programs/stores/programs/programs.store'
import { getUserProgramRoles, V1_USER_PROGRAM_ROLES_QUERY_KEY } from '@/features/users/api'
import checkPermissionsGroup from '@/features/users/utils'
import useRouter from '@/hooks/use-router.hook'
import useToast from '@/hooks/use-toasts.hook'
import { useI18n } from '@/lib/i18n'

const { t } = useI18n()
const { accountRef } = useAccount()
const { programsRef } = usePrograms()
const selectedProgramIdRef = selectGetSelectedProgramId(programsStore)
const router = useRouter()
const toast = useToast()

const newlySelectedProgramIdRef = ref<string | undefined>(undefined)
const isProgramQueryEnabledRef = computed(() => !!newlySelectedProgramIdRef.value)

const programRolesQueryResult = useQuery({
  queryKey: [V1_USER_PROGRAM_ROLES_QUERY_KEY, { programId: newlySelectedProgramIdRef, userId: accountRef.value!.id! }],
  queryFn: () => getUserProgramRoles({ programId: newlySelectedProgramIdRef.value!, userId: accountRef.value!.id! }),
  enabled: isProgramQueryEnabledRef,
})

const tempProgramRolesRef = computed(() => {
  programsStore.setIsProgramDataLoading(false)
  return programRolesQueryResult.data.value?.roles
})

watch(tempProgramRolesRef, (tempProgramRoles) => {
  const userActivePermissionGroup = authStore.userActivePermissionGroup
  if (!userActivePermissionGroup) return
  if (!newlySelectedProgramIdRef.value) return

  programsStore.setSelectedProgramId(newlySelectedProgramIdRef.value)
  newlySelectedProgramIdRef.value = undefined

  if (!checkPermissionsGroup(tempProgramRoles, userActivePermissionGroup)) {
    toast?.error(t('redirect.notification.interface'))
    authStore.setUserActivePermissionGroup(null)
    router?.push({ name: 'Redirect' })
  }
})

const handleSwitchProgram = (programId: string) => {
  // Ne rien faire si on re-sélectionne le même programme
  if (programId === selectedProgramIdRef.value) return

  newlySelectedProgramIdRef.value = programId
  programsStore.setIsProgramDataLoading(true)
}
</script>
